import { useEffect, useMemo, useState } from "react";

const WAITING_WINDOW_SECONDS = 600;

export const UI_STATE = {
  IDLE: "IDLE",
  WAITING: "WAITING",
  CONNECTING: "CONNECTING",
  IN_CALL: "IN_CALL",
  RECONNECTING: "RECONNECTING",
  ENDED: "ENDED",
};

function parseStoredDateTime(value) {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed;
}

function participantsByRole(participants) {
  const map = {
    candidate: null,
    interviewer: null,
  };

  (participants || []).forEach((participant) => {
    if (participant?.user_type === "candidate") {
      map.candidate = participant;
    }
    if (participant?.user_type === "interviewer") {
      map.interviewer = participant;
    }
  });

  return map;
}

export default function useInterviewStateMachine({ roomState, connectionState }) {
  const [serverOffsetMs, setServerOffsetMs] = useState(0);
  const [waitingRemainingSeconds, setWaitingRemainingSeconds] = useState(null);
  const scheduledEndDateTime = useMemo(
    () => parseStoredDateTime(localStorage.getItem("externalInterviewEndDateTime")),
    []
  );

  useEffect(() => {
    if (!roomState?.server_now) {
      return;
    }
    setServerOffsetMs(Date.now() - roomState.server_now * 1000);
  }, [roomState?.server_now, roomState?.version]);

  useEffect(() => {
    if (!roomState?.waiting_started_at) {
      setWaitingRemainingSeconds(null);
      return undefined;
    }

    const tick = () => {
      const serverNow = (Date.now() - serverOffsetMs) / 1000;
      const elapsed = serverNow - roomState.waiting_started_at;
      const waitingWindowRemaining = Math.max(
        0,
        Math.floor(WAITING_WINDOW_SECONDS - elapsed)
      );
      const scheduledRemaining = scheduledEndDateTime
        ? Math.max(
            0,
            Math.floor((scheduledEndDateTime.getTime() - Date.now()) / 1000)
          )
        : waitingWindowRemaining;
      const remaining = Math.min(waitingWindowRemaining, scheduledRemaining);
      setWaitingRemainingSeconds(remaining);
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [
    roomState?.waiting_started_at,
    roomState?.waiting_cycle,
    scheduledEndDateTime,
    serverOffsetMs,
  ]);

  const participantMap = useMemo(
    () => participantsByRole(roomState?.participants || []),
    [roomState?.participants]
  );

  const uiState = useMemo(() => {
    if (!roomState) {
      return connectionState === "connected" ? UI_STATE.IDLE : UI_STATE.CONNECTING;
    }

    if (roomState.status === "ended") {
      return UI_STATE.ENDED;
    }

    if (connectionState === "reconnecting" || roomState.status === "reconnecting") {
      return UI_STATE.RECONNECTING;
    }

    if (roomState.status === "active") {
      return UI_STATE.IN_CALL;
    }

    if (roomState.status === "waiting") {
      return UI_STATE.WAITING;
    }

    if (roomState.status === "idle") {
      return roomState.participants?.length ? UI_STATE.WAITING : UI_STATE.IDLE;
    }

    return UI_STATE.IDLE;
  }, [connectionState, roomState]);

  const showWaitingActions =
    roomState?.status === "waiting" &&
    roomState?.waiting_started_at &&
    waitingRemainingSeconds === 0;

  return {
    uiState,
    participantMap,
    waitingRemainingSeconds,
    showWaitingActions,
  };
}
